<template>
	<card outlined :style="{borderColor: props.active ? `${$config.public.colors.success} !important` : undefined}">
		<resource-list-item :description="price.description" divided>

			<template #title>
				<div class="flex items-end">
					<p class="font-semi-bold text-success mb-0 leading-none">{{ price.cents > 0 ? toEuro(price.cents) : $t('label.free') }}</p>
					<span class="text-subtext-color text-4xs leading-none ml-8">{{ price.tax_percent && price.tax_percent > 0 ? $t('text.including_tax_and_fees', {tax: price.tax_percent}) : $t('text.including_fees') }}</span>
				</div>
			</template>

			<template v-if="props.active || props.inactive" #right>
				<chip v-if="props.active" color="success" x-small class="ml-8">{{ $t('label.active') }}</chip>
				<chip v-else-if="props.inactive" x-small class="ml-8">{{ $t('label.inactive') }}</chip>
			</template>

			<info-list v-if="price.min_age || price.max_age || price.max_purchases || price.start_date || price.end_date" x-small>

				<info-list-item v-if="price.min_age && price.max_age" :icon="false" :title="$t('label.age')"
								:text="`${price.min_age} - ${price.max_age} ${$t('label.years')}`"/>
				<info-list-item v-else-if="price.min_age" :icon="false" :title="$t('label.age')"
								:text="`${$t('conjunction.starting_at')} ${price.min_age} ${$t('label.years')}`"/>
				<info-list-item v-else-if="price.max_age" :title="$t('label.age')" :icon="false" :text="`${$t('conjunction.until')} ${price.max_age} ${$t('label.years')}`"/>

				<info-list-item v-if="purchasesRangeStart && purchasesRangeEnd" :icon="false"
								:text="$t('text.valid_for_purchase_range', {start: purchasesRangeStart.toLocaleString($i18n?.localeProperties?.iso ?? 'de-DE'), end: purchasesRangeEnd.toLocaleString($i18n?.localeProperties?.iso ?? 'de-DE')})"/>
				<info-list-item v-else-if="price.max_purchases" :icon="false" :color="active ? 'warning' : null"
								:text="$t('param_text.valid_for_the_next_count_purchases', price.max_purchases, {count: price.max_purchases.toLocaleString($i18n?.localeProperties?.iso ?? 'de-DE')})"/>
				<info-list-item v-if="active && price.available_purchases_count !== null" :icon="false" :color="active ? 'warning' : null"
								:text="$t('param_text.valid_for_count_remaining_purchases', price.available_purchases_count, {count: price.available_purchases_count.toLocaleString($i18n?.localeProperties?.iso ?? 'de-DE')})"/>
				<info-list-item v-if="price.end_date && (!price.start_date || (active && $date(price.start_date).isBefore()))" :icon="false" :color="active ? 'success' : null"
								:text="$t('text.price.window_end', {end: $date(price.end_date).format('LLL')})"/>
				<info-list-item v-else-if="price.start_date && price.end_date" :icon="false" :color="active ? 'success' : null"
								:text="$t('text.price.window', {start: $date(price.start_date).format('LL'), end: $date(price.end_date).format('LL')})"/>
				<info-list-item v-else-if="price.start_date" :icon="false" :text="$t('text.price.window_start', {start: $date(price.start_date).format('LL')})"/>
			</info-list>

		</resource-list-item>
	</card>
</template>

<script setup lang="ts">
import {toEuro} from "@spoferan/spoferan-ts-core";
import {computed} from "vue";

const props = defineProps<{
	price: object,

	// The other prices of the same set (participation class)
	// Required to display a user-friendly max_purchases info
	setPrices: Array<{
		cents: number,
		max_purchases: number
	}>,

	// Whether an active layout shall be displayed on the price
	active: boolean,

	// Whether an inactive layout shall be displayed on the price
	inactive: boolean,
}>();

const purchasesRangeStart = computed(() => {
	if (props.setPrices && props.setPrices.length) {
		const prevPrices = props.setPrices.filter(price => price.cents < props.price.cents && price.max_purchases);

		return prevPrices && prevPrices.length ? prevPrices.map(price => price.max_purchases).reduce((value, currentValue) => value + currentValue, 1) : 1;
	}
	return null;
});

const purchasesRangeEnd = computed(() => {
	return props.price.max_purchases && purchasesRangeStart.value ? purchasesRangeStart.value + props.price.max_purchases - 1 : null;
});

</script>